import { Component, OnInit } from '@angular/core';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft, lucideEye, lucideSave,
  lucideSettings, lucideReceipt, lucideCode2,
  lucideRefreshCw, lucideSearch, lucideList,
  lucideMaximize2, lucideMinimize2, lucideHelpCircle
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmInput } from '@spartan-ng/helm/input';

import { PageHeaderComponent, StatusBadgeComponent } from '../../../shared/ui/components';
import { QuoteTemplateService } from '../../../services/forwarding/quote-template.service';
import { ContainerLoadTypesService, ContainerLoadType } from '../../../services/forwarding/container-load-types.service';
import { IncotermService } from '../../../services/forwarding/incoterm.service';
import { OperationalChargesService } from '../../../services/forwarding/operational-charges.service';
import { QuoteTemplate } from '../../../models/forwarding/quote-template.model';
import { Incoterm } from '../../../models/forwarding/incoterm.model';
import { OperationalCharge } from '../../../models/forwarding/operational-charge.model';

export const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ' +
  'shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

@Component({
  selector: 'app-edit-quote-template',
  standalone: true,
  imports: [FormsModule, RouterModule, NgTemplateOutlet, TitleCasePipe,
            NgIcon, HlmIcon, HlmButton, HlmInput,
            PageHeaderComponent, StatusBadgeComponent],
  providers: [provideIcons({
    lucideArrowLeft, lucideEye, lucideSave,
    lucideSettings, lucideReceipt, lucideCode2,
    lucideRefreshCw, lucideSearch, lucideList,
    lucideMaximize2, lucideMinimize2, lucideHelpCircle
  })],
  templateUrl: './edit-quote-template.html',
  styleUrls: ['./edit-quote-template.css']
})
export class EditQuoteTemplateComponent implements OnInit {
  templateId!: number;
  template: QuoteTemplate | null = null;
  loading = true;
  saving = false;
  activeTab: 'settings' | 'charges' | 'html' = 'settings';

  readonly selectClass = SELECT_CLASS;

  // Form data
  editForm = {
    name: '',
    shipmentMode: 'SEA',
    containerLoadType: '',
    incotermScope: '',
    status: 'active',
    isDefault: false,
    defaultChargeCodes: [] as string[],
    operationalChargeIds: [] as number[],
    htmlContent: ''
  };

  // Data sources
  containerLoadTypes: ContainerLoadType[] = [];
  incoterms: Incoterm[] = [];
  operationalCharges: OperationalCharge[] = [];
  filteredOperationalCharges: OperationalCharge[] = [];
  operationalChargeSearchTerm = '';

  // Default charge codes
  defaultChargeCodes = [
    { code: 'OF', label: 'Origin Freight' },
    { code: 'OT', label: 'Origin Terminal Handling' },
    { code: 'DC', label: 'Destination Charges' },
    { code: 'IN', label: 'Insurance' },
    { code: 'PC', label: 'Pre-Carriage' }
  ];

  // Placeholder controls
  showPlaceholders = false;
  editorExpanded = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private quoteTemplateService: QuoteTemplateService,
    private containerLoadTypesService: ContainerLoadTypesService,
    private incotermService: IncotermService,
    private operationalChargesService: OperationalChargesService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.templateId = +params['id'];
      this.loadTemplate();
    });

    this.loadContainerLoadTypes();
    this.loadIncoterms();
    this.loadOperationalCharges();
  }

  loadTemplate(): void {
    this.loading = true;
    this.quoteTemplateService.getById(this.templateId).subscribe({
      next: (template) => {
        this.template = template;
        this.editForm = {
          name: template.name,
          shipmentMode: template.shipmentMode,
          containerLoadType: template.containerLoadType || '',
          incotermScope: template.incotermScope || '',
          status: template.status,
          isDefault: template.isDefault,
          defaultChargeCodes: template.defaultChargeCodes || [],
          operationalChargeIds: template.operationalChargeIds || [],
          htmlContent: template.htmlContent || this.getDefaultHtmlTemplate()
        };
        this.loading = false;
      },
      error: () => {
        alert('Failed to load template');
        this.router.navigate(['/dashboard/forwarding/admin']);
        this.loading = false;
      }
    });
  }

  loadContainerLoadTypes(): void {
    this.containerLoadTypesService.getLoadTypes().subscribe({
      next: (types: ContainerLoadType[]) => {
        this.containerLoadTypes = types;
      },
      error: () => {}
    });
  }

  loadIncoterms(): void {
    this.incotermService.getIncoterms().subscribe({
      next: (incoterms) => {
        this.incoterms = incoterms;
      },
      error: () => {}
    });
  }

  loadOperationalCharges(): void {
    this.operationalChargesService.getCharges().subscribe({
      next: (charges) => {
        this.operationalCharges = charges.filter(c => c.isActive);
        this.filteredOperationalCharges = this.operationalCharges;
      },
      error: () => {}
    });
  }

  saveTemplate(): void {
    if (!this.editForm.name.trim()) {
      alert('Please enter a template name');
      return;
    }

    this.saving = true;
    this.quoteTemplateService.update(this.templateId, this.editForm).subscribe({
      next: () => {
        alert('Template updated successfully!');
        this.router.navigate(['/dashboard/forwarding/admin']);
      },
      error: () => {
        alert('Failed to update template');
        this.saving = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/dashboard/forwarding/admin']);
  }

  previewTemplate(): void {
    alert('Preview functionality coming soon!');
  }

  setActiveTab(tab: 'settings' | 'charges' | 'html'): void {
    this.activeTab = tab;
  }

  isChargeCodeSelected(code: string): boolean {
    return this.editForm.defaultChargeCodes.includes(code);
  }

  toggleChargeCode(code: string): void {
    const index = this.editForm.defaultChargeCodes.indexOf(code);
    if (index > -1) {
      this.editForm.defaultChargeCodes.splice(index, 1);
    } else {
      this.editForm.defaultChargeCodes.push(code);
    }
  }

  isOperationalChargeSelected(chargeId: number): boolean {
    return this.editForm.operationalChargeIds.includes(chargeId);
  }

  toggleOperationalCharge(chargeId: number): void {
    const index = this.editForm.operationalChargeIds.indexOf(chargeId);
    if (index > -1) {
      this.editForm.operationalChargeIds.splice(index, 1);
    } else {
      this.editForm.operationalChargeIds.push(chargeId);
    }
  }

  filterOperationalCharges(): void {
    if (!this.operationalChargeSearchTerm.trim()) {
      this.filteredOperationalCharges = this.operationalCharges;
      return;
    }

    const searchLower = this.operationalChargeSearchTerm.toLowerCase();
    this.filteredOperationalCharges = this.operationalCharges.filter(charge =>
      charge.chargeCode.toLowerCase().includes(searchLower) ||
      (charge.description && charge.description.toLowerCase().includes(searchLower)) ||
      charge.feeName.toLowerCase().includes(searchLower)
    );
  }

  refreshOperationalCharges(): void {
    this.operationalChargesService.refresh();
    this.loadOperationalCharges();
  }

  getSelectedOperationalChargesForDisplay(): OperationalCharge[] {
    return this.operationalCharges.filter(charge =>
      this.editForm.operationalChargeIds.includes(charge.id)
    );
  }

  togglePlaceholders(): void {
    this.showPlaceholders = !this.showPlaceholders;
  }

  toggleExpandEditor(): void {
    this.editorExpanded = !this.editorExpanded;
  }

  getStatusVariant(status: string): 'success' | 'neutral' | 'default' {
    if (status === 'active') return 'success';
    if (status === 'inactive') return 'neutral';
    return 'default';
  }

  getDefaultHtmlTemplate(): string {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    .header { text-align: center; margin-bottom: 30px; }
    .info-section { margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #f2f2f2; }
    .total { font-weight: bold; text-align: right; }
  </style>
</head>
<body>
  <div class="header">
    <h1>FREIGHT QUOTATION</h1>
    <p>Quote Number: {{meta.quote_number}}</p>
    <p>Date: {{meta.date_issued_display}}</p>
  </div>

  <div class="info-section">
    <h3>Client Information</h3>
    <p><strong>Name:</strong> {{client.name}}</p>
    <p><strong>Address:</strong> {{client.address}}</p>
    <p><strong>Email:</strong> {{client.email}}</p>
  </div>

  <div class="info-section">
    <h3>Shipment Details</h3>
    <p><strong>Origin:</strong> {{ports.origin.name}} ({{ports.origin.code}})</p>
    <p><strong>Destination:</strong> {{ports.destination.name}} ({{ports.destination.code}})</p>
    <p><strong>Mode:</strong> {{shipment.mode}}</p>
    <p><strong>Container Type:</strong> {{shipment.container_load_type}}</p>
    <p><strong>Incoterm:</strong> {{incoterm.code}}</p>
  </div>

  <h3>Charges</h3>
  <table>
    <thead>
      <tr>
        <th>Charge</th>
        <th>Basis</th>
        <th>Unit Rate</th>
        <th>Currency</th>
        <th>Amount</th>
      </tr>
    </thead>
    <tbody>
      <!-- Charges will be inserted here -->
    </tbody>
    <tfoot>
      <tr class="total">
        <td colspan="4">Total</td>
        <td>{{totals.currency}} {{totals.grand_total}}</td>
      </tr>
    </tfoot>
  </table>

  <div class="info-section">
    <p><strong>Valid Until:</strong> {{meta.valid_until_display}}</p>
    <p><strong>Notes:</strong> {{meta.notes}}</p>
  </div>
</body>
</html>`;
  }
}
